/**
 * Which session the credential hook may still answer for, and the one call that ends it.
 *
 * The hook outlives renders. Removing it from the host config only stops a LATER lookup: a request
 * Zoom already started keeps its own reference, awaits the network, and resumes after sign-out but
 * before React has run the effect that blanks the widget's session ref. So the hook cannot rely on
 * that ref to know the session ended; it asks this counter instead, which moves in the same
 * synchronous step as the sign-out itself.
 *
 * A leaf module on purpose, like clearZoomChatIdentity and zoomChatSdk: the store calls
 * `revokeZoomChatIdentity` from its sign-out middleware, and the feature barrel reaches the widget,
 * which imports the store. Nothing here may import the store, or anything that does.
 *
 * Module scope, for the reason `evictionOwed` lives there: the thing being protected is Zoom's
 * executed client, whose lifetime is this document's. The counter only ever goes up, so a hook
 * registered under an earlier generation can never be made valid again by a later session.
 */
let identityGeneration = 0;

/**
 * Who wants to hear that the generation moved: the widget that owns the hook, through
 * `useSyncExternalStore`. A revocation takes the hook out of the host config at once, but the
 * session that follows it can carry the same patient id (a patient who verifies again as themselves
 * is cleared and set again in one task, so nothing the widget reads from the store changes), and
 * the widget has to learn from here that the hook it registered is gone.
 */
const listeners = new Set<() => void>();

/** The generation a hook registers under; it is valid only while this still returns the same value. */
export const getZoomChatIdentityGeneration = () => identityGeneration;

/**
 * Calls `listener` every time the generation moves, and returns the function that stops it. The
 * shape `useSyncExternalStore` takes. A listener reads the new generation through
 * `getZoomChatIdentityGeneration`: it is told after the change, never before.
 */
export const subscribeToZoomChatIdentityGeneration = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * Ends the current generation and takes the hook out of the host config, synchronously. Called on
 * `user/clearUser`, which covers sign-out, a refresh failure and every flow that replaces one
 * patient with another. The rest of the host config is left alone, because Zoom or another
 * integration may have set it.
 *
 * Does not end the engagement, clear storage or reload: those stay with the middleware's other
 * steps and `useLogout`. Subscribers are told last, once the generation has moved and the hook is
 * out, so a widget that registers a hook for the next session does so under the new generation. For
 * a React subscriber, being told only schedules a render, and the dispatch that called this is
 * still synchronous: the render sees the store after the reducer, not before it.
 */
export const revokeZoomChatIdentity = () => {
  identityGeneration += 1;

  try {
    delete window.zoomCampaignSdkConfig?.getAuthCredentials;
  } catch {
    // A frozen or replaced config cannot be edited, but the generation above has moved, so a hook
    // that is still reachable refuses on its own.
  }

  listeners.forEach((listener) => listener());
};
